import { useMemo } from 'react'

import { useOrganizationUiSetup } from '../../../hooks/useOrganizationUiSetup'
import { InvoiceDocumentType } from '../enums/invoiceDocumentType'
import { InvoiceEditorVersion } from '../enums/invoiceEditorVersion'
import { InvoiceStatus } from '../enums/invoiceStatus'
import { Invoice } from '../types/invoices/invoice'
import { hasInvoiceSentStatus } from '../utils/hasInvoiceSentStatus'
import { isDocumentTypeCreditNote } from '../utils/isDocumentTypeCreditNote'
import { isDocumentTypeInvoice } from '../utils/isDocumentTypeInvoice'
import { isInvoiceManuallyMarkedAsSent } from '../utils/isInvoiceManuallyMarkedAsSent'
import { useFetchExternalInvoiceFromBilly } from './useFetchExternalInvoiceFromBilly'

// Deleted and Draft invoices cannot be cancelled; Paid invoices require a credit note instead of cancellation
const nonCancellableInvoiceStatuses = [InvoiceStatus.Deleted, InvoiceStatus.Draft, InvoiceStatus.Paid]
const nonMarkableAsSentStatuses = [InvoiceStatus.Draft, InvoiceStatus.Sent]

const INVOICE_ACTION_BUTTONS_DISPLAY_LIMIT = 2
const CREDIT_NOTE_ACTION_BUTTONS_DISPLAY_LIMIT = 1

interface UseInvoiceActionFlagsParams {
  invoice: Invoice | undefined
  documentType: InvoiceDocumentType
}

export const useInvoiceActionFlags = ({ invoice, documentType }: UseInvoiceActionFlagsParams) => {
  const { isUiSetupShineInvoicingAccounting, isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()

  const externalId = invoice?.externalInvoiceId || invoice?.externalCreditNoteId || ''
  const { externalInvoice: billyApiExternalInvoice, isLoading: isBillyExternalInvoiceLoading } =
    useFetchExternalInvoiceFromBilly(externalId)
  const isExternalInvoiceLoading = !!externalId && isBillyExternalInvoiceLoading

  const invoiceStatus = invoice?.status?.status
  const isCreatedInAgeras = invoice?.version === InvoiceEditorVersion.Ageras
  const isInvoiceDocument = isDocumentTypeInvoice(documentType)
  const isCreditNoteDocument = isDocumentTypeCreditNote(documentType)

  const isCreditedInvoice = isInvoiceDocument && !!invoice?.referredCreditnoteId
  const isCreditNoteUnrelated = !isCreditedInvoice && !isCreditNoteDocument
  const isCancellableStatus = invoiceStatus && !nonCancellableInvoiceStatuses.includes(invoiceStatus)
  const isInvoiceDeleted = invoiceStatus === InvoiceStatus.Deleted
  const isInvoicePaid = isInvoiceDocument && invoiceStatus === InvoiceStatus.Paid

  /** Cancellation & duplication rules differ by UI setup:
   * - Supernav: only Ageras invoices are allowed
   * - Shine: any eligible invoice is allowed
   */
  const isActionAllowedByUiSetup =
    (isUiSetupSupernavInvoicingAccounting && isCreatedInAgeras) || isUiSetupShineInvoicingAccounting
  const statusEvents = invoice?.status?.events ?? []
  const isManuallyMarkedAsSent = isInvoiceManuallyMarkedAsSent(statusEvents)
  const isSent = hasInvoiceSentStatus(statusEvents)

  /** We evaluate based on the Invoice record on billy because:
   * 1. The reconciliation functionality happens on Billy side
   * 2. If the payment is partially reconciled, the Zervant status will still be "Paid"
   */
  const hasBalanceToReconcileInBilly = !!billyApiExternalInvoice?.balance
  const hasPaymentReconciledWith = !!billyApiExternalInvoice?.balanceModifiers?.length

  const canRegisterPayment = isCreditNoteUnrelated && hasBalanceToReconcileInBilly && !isInvoiceDeleted
  const canSendPaymentReminder = canRegisterPayment && isCancellableStatus && isInvoiceDocument

  const shouldDisplayOpenInZervantAction = !isCreatedInAgeras && isUiSetupSupernavInvoicingAccounting
  // Credited invoices and credit notes are excluded; paid invoices require a credit note instead
  const isMarkableAsSent =
    isCreditNoteUnrelated &&
    invoiceStatus &&
    !isInvoiceDeleted &&
    !nonMarkableAsSentStatuses.includes(invoiceStatus) &&
    !isSent &&
    !isInvoicePaid

  const isUnmarkableAsSent = !isCreditedInvoice && !isInvoiceDeleted && isManuallyMarkedAsSent
  const isDuplicable = isInvoiceDocument && isActionAllowedByUiSetup
  const isCancellable =
    isCreditNoteUnrelated && isCancellableStatus && !hasPaymentReconciledWith && isActionAllowedByUiSetup
  const isFullyReconciled = isInvoiceDocument && !hasBalanceToReconcileInBilly && hasPaymentReconciledWith
  const canCancelPaidInvoiceWithCreditNote = (isInvoicePaid || isFullyReconciled) && isActionAllowedByUiSetup
  const canCreateCreditNote =
    (isCancellable || canCancelPaidInvoiceWithCreditNote) && !invoice?.referredCreditnoteId && isInvoiceDocument

  const actionButtonDisplayLimit = isCreditNoteDocument
    ? CREDIT_NOTE_ACTION_BUTTONS_DISPLAY_LIMIT
    : INVOICE_ACTION_BUTTONS_DISPLAY_LIMIT

  return useMemo(
    () => ({
      actionButtonDisplayLimit,
      canCreateCreditNote,
      canRegisterPayment,
      canSendPaymentReminder,
      isCancellable,
      isDuplicable,
      isExternalInvoiceLoading,
      isMarkableAsSent,
      isUnmarkableAsSent,
      shouldDisplayOpenInZervantAction,
    }),
    [
      actionButtonDisplayLimit,
      canCreateCreditNote,
      canRegisterPayment,
      canSendPaymentReminder,
      isCancellable,
      isDuplicable,
      isExternalInvoiceLoading,
      isMarkableAsSent,
      isUnmarkableAsSent,
      shouldDisplayOpenInZervantAction,
    ],
  )
}
